import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Pasi } from "./Pasi";
import { regimEfectiv } from "./regim";
import { citesteLocalizare, salveazaLocalizare, srcPozaFatada, urcaPozaFatada, TEMA_GOALA,
         type AlegereFatada, type Localizare as Loc, type ReferintaFatada,
         type SistemFatada, type Tema } from "./dosarclient";
import { IMAGINI, PALETA, RECOMANDATE, STILURI, imagineMare, imagineMica,
         type ImagineFatada, type Stil } from "./fatada/catalog";
import { ALEGERE_GOALA, MAX_REFERINTE, adaugaPoza, alegereaNoastra, comuta, laNivel,
         micsoreaza, pozitia, rezumat, sistemDinStandard } from "./fatada/alegere";
import * as I from "../components/icons";
import { ApiError, startJob } from "../api";
import { useRegistruOptional } from "../nav/Registru";
import { BandaDoarCitire, useRolProiect } from "../components/rol";
import { bcp47, limba, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import "./fatada.css";

// Pasul 4: CUM ARATĂ clădirea.
//
// Două întrebări, separate, fiindcă sunt independente: din ce se face fațada
// (sistemul — costul și grosimea) și ce expresie are (stilul). Stilul nu se
// alege dintr-o listă de nume: omul alege între una și trei imagini care îi
// plac, iar stilul îl deducem noi din ele. Numele stilului e eticheta
// grupului, nu întrebarea.
//
// Alegerea se ține în temă (`tema.fatada`); o citește AGENTUL DE FAȚADĂ
// (serviciul `facade-agent`), pornit de lanț după etajele fiecărei arhitecturi:
// el deduce stilul cu clădirea în față și îl pune pe soluție de la început.
// De aici pornește generarea, ca tot ce pleacă la motor să fie hotărât într-un
// singur loc.

type Tab = "recomandate" | "stiluri" | "toate";

const SISTEME: { k: SistemFatada; cost: number; straturi: [string, number][] }[] = [
  { k: "etics", cost: 1, straturi: [["#d9d2c8", 62], ["#f2efe9", 38]] },
  { k: "etics_placari", cost: 2, straturi: [["#d9d2c8", 45], ["#f2efe9", 25], ["#b88a67", 30]] },
  { k: "ventilata", cost: 3, straturi: [["#c9c3bb", 40], ["#ffffff", 10], ["#7d6a58", 50]] },
];

const PE_STIL = new Map<Stil, ImagineFatada[]>(
  STILURI.map((s) => [s, IMAGINI.filter((i) => i.stil === s)]));
const IMAGINEA = new Map(IMAGINI.map((i) => [i.id, i]));

const numeStil = (s: string) => `fat.stil.${s}` as Cheie;

export default function Fatada() {
  const t = useT();
  const tn = useTn();
  const { projectId } = useParams();
  const pid = projectId as string;
  const nav = useNavigate();
  const registrul = useRegistruOptional();
  const { potScrie } = useRolProiect();
  const [loc, setLoc] = useState<Loc | null>(null);
  const [gata, setGata] = useState(false);
  const [err, setErr] = useState("");
  const [pornesc, setPornesc] = useState(false);
  const [tab, setTab] = useState<Tab>("recomandate");
  const [filtru, setFiltru] = useState<Stil | null>(null);
  const [toast, setToast] = useState("");
  const [peste, setPeste] = useState(false);
  // Mărirea: lista prin care se merge cu săgețile și poziția în ea. O intrare
  // e un id din catalog sau `poza:<k>` pentru a k-a poză încărcată.
  const [lupa, setLupa] = useState<{ lista: string[]; i: number } | null>(null);

  // Alegerea se ține pe ecran și se trimite în fundal, pe rând: două clicuri
  // repezi nu trebuie să ajungă la server în ordine inversă.
  const [alegere, setAlegere] = useState<AlegereFatada>(ALEGERE_GOALA);
  const coada = useRef<Promise<void>>(Promise.resolve());
  const temaRef = useRef<Tema>(TEMA_GOALA);

  useEffect(() => {
    citesteLocalizare(pid)
      .then((l) => {
        setLoc(l);
        temaRef.current = l?.tema || TEMA_GOALA;
        setAlegere(l?.tema?.fatada || ALEGERE_GOALA);
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setGata(true));
  }, [pid]);

  const scrie = useCallback((p: Partial<Tema>) => {
    temaRef.current = { ...temaRef.current, ...p };
    const tema = temaRef.current;
    coada.current = coada.current.then(async () => {
      try {
        setLoc(await salveazaLocalizare(pid, { tema }));
        setErr("");
      } catch (e) {
        setErr(String(e));
      }
    });
    return coada.current;
  }, [pid]);

  const puneAlegere = (a: AlegereFatada) => {
    setAlegere(a);
    scrie({ fatada: a });
  };
  const puneRefs = (refs: ReferintaFatada[]) => {
    setToast("");
    puneAlegere({ ...alegere, referinte: refs });
  };

  const nivel = loc?.tema?.calitate?.fatada || loc?.tema?.calitate?.nivel || null;
  const sistem = alegere.sistem ?? sistemDinStandard(nivel);
  const refs = alegere.referinte;
  // catalogul, pe fiecare stil, cu imaginile nivelului clădirii întâi
  const peStil = useMemo(() => new Map(STILURI.map((s) => [s, laNivel(PE_STIL.get(s) || [], nivel)])),
                         [nivel]);

  const alege = (id: string) => {
    if (!potScrie) return;
    const r = comuta(refs, id);
    if (r.plin) { setToast(t("fat.tava.plin")); return; }
    puneRefs(r.refs);
  };

  const incarca = async (f: File | undefined) => {
    if (!f || !potScrie) return;
    if (!f.type.startsWith("image/")) { setToast(t("fat.incarca_eroare")); return; }
    if (refs.length >= MAX_REFERINTE) { setToast(t("fat.tava.plin")); return; }
    try {
      // URCATĂ O DATĂ, aici; în temă intră doar id-ul ei
      const id = await urcaPozaFatada(pid, f.name, await micsoreaza(f));
      const r = adaugaPoza(refs, id, f.name);
      puneRefs(r.refs);
    } catch {
      setToast(t("fat.incarca_eroare"));
    }
  };

  const areContur = !!(loc?.contur_wgs84?.length || loc?.contur_stereo70?.length);
  const areZone = (loc?.zone_acces || []).length > 0;
  const potGenera = areContur && areZone && potScrie;

  const porneste = async () => {
    setErr("");
    setPornesc(true);
    try {
      // Ce a văzut omul pe ecran devine decizie ACUM: regimul propus în temă și,
      // fără nicio fațadă aleasă, cele trei direcții ale noastre.
      const tema = temaRef.current;
      const r = regimEfectiv(tema.regim_inaltime, loc?.regim_urbanistic);
      const p: Partial<Tema> = {};
      if (r.presetat) p.regim_inaltime = r.regim;
      if (!refs.length) {
        const a = { ...alegere, referinte: alegereaNoastra() };
        setAlegere(a);
        p.fatada = a;
      }
      if (Object.keys(p).length) await scrie(p);
      await startJob(pid, "site_plan");
      // recitit acum, ca segmentul gol să vadă rularea nouă și să arate semnul
      // care se construiește, nu «Nicio soluție»
      await registrul?.reciteste();
      // Direct în vizualizator, pe planul de situație: segmentul gol arată
      // semnul care se construiește și se deschide singur pe primul plan cât
      // de repede iese; lanțul din antet spune ce urmează după el.
      nav(`/p/${pid}/situatie`);
    } catch (e) {
      setErr(e instanceof ApiError && e.message ? e.message : t("loc.esec_generare"));
    } finally {
      setPornesc(false);
    }
  };

  const deschideStil = (s: Stil) => { setFiltru(s); setTab("toate"); };
  const stilAles = (s: Stil) => refs.some((r) => r.sursa === "catalog" && r.stil === s);
  const comutaStil = (s: Stil) => {
    if (!potScrie) return;
    if (stilAles(s)) {
      puneRefs(refs.filter((r) => !(r.sursa === "catalog" && r.stil === s)));
      return;
    }
    alege(peStil.get(s)![0].id);
  };

  // Lista prin care merge mărirea: ce se vede acum pe ecran.
  const vizibile = useMemo(() => {
    if (tab === "recomandate") return RECOMANDATE;
    if (tab === "toate" && filtru) return (peStil.get(filtru) || []).map((i) => i.id);
    return laNivel(IMAGINI, nivel).map((i) => i.id);
  }, [tab, filtru, peStil, nivel]);
  const deschide = (id: string, lista = vizibile) =>
    setLupa({ lista, i: Math.max(0, lista.indexOf(id)) });

  if (!gata) {
    return (
      <div className="gol" style={{ height: "100%" }}>
        <span className="lbl">{t("loc.se_incarca")}</span>
      </div>
    );
  }

  // Funcții de desenat, nu componente: o componentă definită în corpul altei
  // componente e alt tip la fiecare randare, iar React ar remonta toată grila
  // (și ar pierde focalizarea) la fiecare clic.
  const tile = (id: string, eticheta = false) => {
    const img = IMAGINEA.get(id)!;
    const k = pozitia(refs, id);
    return (
      <div key={id} className={`ft-tile${k >= 0 ? " ales" : ""}`}>
        <button type="button" className="ft-tile-img" aria-pressed={k >= 0}
                aria-label={t(numeStil(img.stil))}
                onClick={() => (potScrie ? alege(id) : deschide(id))}>
          <img src={imagineMica(id)} alt="" loading="lazy" />
          {potScrie && <span className="ft-nr">{k >= 0 ? k + 1 : <I.Plus size={12} />}</span>}
        </button>
        <button type="button" className="ft-lupa-btn" title={t("fat.mareste")}
                aria-label={t("fat.mareste")} onClick={() => deschide(id)}>
          <I.Cauta size={13} />
        </button>
        {eticheta && (
          <button type="button" className="ft-eticheta" onClick={() => deschideStil(img.stil)}>
            {t(numeStil(img.stil))}
          </button>
        )}
      </div>
    );
  };

  const incarcaTile = (lat = false) => (
    <label key="incarca" htmlFor="ft-fisier"
           className={`ft-incarca${lat ? " ft-incarca-lat" : ""}${peste ? " peste" : ""}`}
           onDragEnter={(e) => { e.preventDefault(); setPeste(true); }}
           onDragOver={(e) => { e.preventDefault(); setPeste(true); }}
           onDragLeave={() => setPeste(false)}
           onDrop={(e) => { e.preventDefault(); setPeste(false); incarca(e.dataTransfer.files[0]); }}>
      <I.Urca size={22} />
      <span className="ft-incarca-txt">
        <b>{t("fat.incarca")}</b>
        <small>{t("fat.incarca_sub")}</small>
      </span>
    </label>
  );

  const rez = rezumat(refs);
  const lista = (parti: (Stil | "poza")[]) =>
    new Intl.ListFormat(bcp47(limba()), { type: "conjunction" })
      .format(parti.map((p) => (p === "poza" ? t("fat.tava.poza_ta") : t(numeStil(p)))));
  const mesaj =
    rez.fel === "gol" ? t("fat.tava.gol")
    : rez.fel === "un_stil" ? t("fat.tava.un_stil", { stil: t(numeStil(rez.stil)) })
    : rez.fel === "o_poza" ? t("fat.tava.o_poza")
    : t("fat.tava.mai_multe", { lista: lista(rez.parti) });

  return (
    <div className="tm ft">
      <div className="tm-cap">
        <Pasi pid={pid} activ="facade" />
        <div className="sp" />
        {/* De ce nu se poate genera, scris lângă buton: cel mai des e lotul
            nedesenat de la pasul 2. Chip-ul e și drumul înapoi. */}
        {potScrie && !potGenera && (
          <Link className="chip c-amb tm-lipsa" to={`/p/${pid}/location`}>
            {t(areContur ? "tema.fara_zona" : "tema.fara_parcela")}
          </Link>
        )}
        <button className="btn btnp" style={{ height: 36, fontSize: 13 }}
                disabled={!potGenera || pornesc}
                title={t(!potScrie ? "comun.doar_citire"
                     : !areContur ? "loc.fara_contur"
                     : !areZone ? "loc.fara_zona"
                                 : "loc.porneste_titlu")}
                onClick={porneste}>
          {t(pornesc ? "loc.pornesc" : "loc.porneste")} <I.Sageata size={14} />
        </button>
      </div>

      <BandaDoarCitire />
      {err && <div className="err">{err}</div>}

      <input id="ft-fisier" type="file" accept="image/*" hidden disabled={!potScrie}
             onChange={(e) => { incarca(e.target.files?.[0]); e.target.value = ""; }} />

      <div className="ft-col">
        <section className="card tm-card tm-spune ft-intro">
          <b className="ft-titlu">{t("fat.titlu")}</b>
          <span className="ft-intro-sub">{t("fat.sub")}</span>
        </section>

        {/* --- Sistemul ------------------------------------------------ */}
        <section className="card tm-card">
          <div className="tm-card-cap">
            <b className="tm-titlu">{t("fat.sistem.titlu")}</b>
            <span className="ft-nota">{t("fat.sistem.sub")}</span>
            <div className="sp" />
            {!alegere.sistem && <span className="chip c-out">{t("fat.sistem.din_standard")}</span>}
          </div>
          <div className="ft-sisteme">
            {SISTEME.map((s) => (
              <button key={s.k} type="button" disabled={!potScrie}
                      className={`tm-nivel ft-sistem${sistem === s.k ? " on" : ""}`}
                      onClick={() => puneAlegere({ ...alegere, sistem: s.k })}>
                <span className="ft-sistem-cap">
                  <b>{t(`fat.sistem.${s.k}` as Cheie)}</b>
                  <span className="ft-cost" title={t("fat.sistem.cost")}>
                    {"€".repeat(s.cost)}<s>{"€".repeat(3 - s.cost)}</s>
                  </span>
                </span>
                <span>{t(`fat.sistem.${s.k}_sub` as Cheie)}</span>
                <span className="ft-straturi" aria-hidden="true">
                  {s.straturi.map(([c, w], i) => <i key={i} style={{ background: c, width: `${w}%` }} />)}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* --- Fațadele care îi plac ------------------------------------ */}
        <section className="card tm-card">
          <div className="tm-card-cap">
            <b className="tm-titlu">{t("fat.alege.titlu")}</b>
            <span className="chip c-acc">{t("fat.alege.max")}</span>
            <div className="sp" />
            {potScrie && (
              <label htmlFor="ft-fisier" className="btn">
                <I.Urca size={13} />{t("fat.incarca")}
              </label>
            )}
          </div>
          <div className="seg ft-taburi" role="tablist">
            {(["recomandate", "stiluri", "toate"] as Tab[]).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k}
                      className={tab === k ? "on" : ""}
                      onClick={() => { setTab(k); if (k === "toate") setFiltru(null); }}>
                {t(`fat.tab.${k}` as Cheie)}
              </button>
            ))}
          </div>
          <p className="ft-nota ft-ai-nota">{t("fat.ai_nota")}</p>

          {tab === "recomandate" && (
            <>
              <div className="ft-rand">
                <span className="ft-nota">{t("fat.recomandate.sub")}</span>
                <div className="sp" />
                {potScrie && !refs.length && (
                  <button type="button" className="btn" onClick={() => puneRefs(alegereaNoastra())}>
                    {t("fat.alege_pentru_mine")}
                  </button>
                )}
              </div>
              <div className="ft-grila ft-mari">
                {RECOMANDATE.map((id) => tile(id, true))}
              </div>
            </>
          )}

          {tab === "stiluri" && (
            <div className="ft-stiluri">
              {STILURI.map((s) => {
                const imgs = peStil.get(s) || [];
                const ales = stilAles(s);
                return (
                  <article key={s} className={`ft-stil${ales ? " ales" : ""}`}>
                    <button type="button" className="ft-mozaic" onClick={() => deschideStil(s)}
                            aria-label={t(numeStil(s))}>
                      {imgs.slice(0, 3).map((i) => <img key={i.id} src={imagineMica(i.id)} alt="" loading="lazy" />)}
                    </button>
                    <div className="ft-stil-corp">
                      <div className="ft-stil-cap">
                        <b>{t(numeStil(s))}</b>
                        <span className="ft-paleta" aria-hidden="true">
                          {PALETA[s].map((c) => <i key={c} style={{ background: c }} />)}
                        </span>
                      </div>
                      <span className="ft-stil-desc">{t(`fat.stil.${s}.desc` as Cheie)}</span>
                      <span className="ft-stil-mat">{t(`fat.stil.${s}.mat` as Cheie)}</span>
                      <div className="ft-stil-jos">
                        <button type="button" className="btn btng" onClick={() => deschideStil(s)}>
                          {tn("fat.stil.vezi#n", imgs.length)}
                        </button>
                        <div className="sp" />
                        {potScrie && (
                          <button type="button" className={`btn${ales ? "" : " btnp"}`}
                                  onClick={() => comutaStil(s)}>
                            {ales ? <><I.Bifa size={12} />{t("fat.stil.ales")}</> : t("fat.stil.alege")}
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {tab === "toate" && (
            <>
              <div className="ft-filtre">
                <button type="button" className={filtru ? "" : "on"} onClick={() => setFiltru(null)}>
                  {t("fat.toate_stilurile")} <em>{IMAGINI.length}</em>
                </button>
                {STILURI.map((s) => (
                  <button key={s} type="button" className={filtru === s ? "on" : ""}
                          onClick={() => setFiltru(s)}>
                    {t(numeStil(s))} <em>{PE_STIL.get(s)?.length}</em>
                  </button>
                ))}
              </div>
              {filtru ? (
                <div className="ft-grup">
                  <div className="ft-grup-cap ft-grup-cap-mare">
                    <b>{t(numeStil(filtru))}</b>
                    <span>{t(`fat.stil.${filtru}.desc` as Cheie)}</span>
                  </div>
                  <div className="ft-grila ft-lata">
                    {potScrie && incarcaTile()}
                    {(peStil.get(filtru) || []).map((i) => tile(i.id))}
                  </div>
                </div>
              ) : (
                <div className="ft-grupuri">
                  {potScrie && incarcaTile(true)}
                  {STILURI.map((s) => {
                    const imgs = peStil.get(s) || [];
                    const rest = imgs.slice(3);
                    return (
                      <div key={s} className="ft-grup">
                        <div className="ft-grup-cap">
                          <b>{t(numeStil(s))}</b>
                          <span>{t(`fat.stil.${s}.desc` as Cheie)}</span>
                        </div>
                        <div className="ft-grila">
                          {imgs.slice(0, 3).map((i) => tile(i.id))}
                          {rest.length > 0 && (
                            <button type="button" className="ft-tile ft-mai" onClick={() => setFiltru(s)}>
                              <span className="ft-pila">
                                {rest.slice(0, 3).map((i) => <img key={i.id} src={imagineMica(i.id)} alt="" loading="lazy" />)}
                              </span>
                              <b>+{rest.length}</b>
                              <span>{tn("fat.mai_multe#n", rest.length)}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* --- Tava: ce a ales, și ce urmează din asta -------------------- */}
      <div className="ft-tava">
        <div className="ft-tava-in">
          <div className="ft-sloturi">
            <span className="lbl">{t("fat.tava.titlu")} · {refs.length}/{MAX_REFERINTE}</span>
            <div className="ft-sloturi-rand">
              {Array.from({ length: MAX_REFERINTE }, (_, k) => {
                const r = refs[k];
                if (!r) {
                  return <span key={k} className="ft-slot" title={t("fat.tava.slot_gol")}>{k + 1}</span>;
                }
                const src = r.sursa === "catalog" ? imagineMica(r.id) : srcPozaFatada(pid, r) ?? "";
                const cheie = r.sursa === "catalog" ? r.id : `poza:${k}`;
                // aleasă înainte ca imaginea să fie scoasă din catalog (fișierul nu mai e pe server)
                const scoasa = r.sursa === "catalog" && !IMAGINEA.has(r.id);
                const deMarit = refs.flatMap((x, j) => (x.sursa !== "catalog" ? [`poza:${j}`]
                  : IMAGINEA.has(x.id) ? [x.id] : []));
                return (
                  <span key={cheie} className="ft-slot plin">
                    <button type="button" className="ft-slot-img" disabled={scoasa}
                            title={scoasa ? t("fat.tava.scoasa_titlu") : undefined}
                            onClick={() => deschide(cheie, deMarit)}>
                      {scoasa ? <span className="ft-slot-lipsa">{t("fat.tava.scoasa")}</span> : <img src={src} alt="" />}
                      <span className="ft-slot-nr">{k + 1}</span>
                    </button>
                    {potScrie && (
                      <button type="button" className="ft-slot-x" title={t("fat.tava.scoate")}
                              aria-label={t("fat.tava.scoate")}
                              onClick={() => puneRefs(refs.filter((_, j) => j !== k))}>
                        <I.X size={10} />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="ft-mesaj">
            <span>{mesaj}</span>
            {toast && <span className="ft-toast">{toast}</span>}
          </div>
          {potScrie && !refs.length && (
            <button type="button" className="btn" onClick={() => puneRefs(alegereaNoastra())}>
              {t("fat.alege_pentru_mine")}
            </button>
          )}
        </div>
      </div>

      {lupa && (
        <Lupa pid={pid} lista={lupa.lista} i={lupa.i} refs={refs} potScrie={potScrie}
              laI={(i) => setLupa({ ...lupa, i })} inchide={() => setLupa(null)}
              alege={alege} />
      )}
    </div>
  );
}

/** Imaginea mărită: originalul întreg, stilul, sursa și alegerea. */
function Lupa({ pid, lista, i, refs, potScrie, laI, inchide, alege }: {
  pid: string; lista: string[]; i: number; refs: ReferintaFatada[]; potScrie: boolean;
  laI: (i: number) => void; inchide: () => void; alege: (id: string) => void;
}) {
  const t = useT();
  const inchideRef = useRef<HTMLButtonElement>(null);
  const cheie = lista[i];
  const poza = cheie.startsWith("poza:") ? refs[Number(cheie.slice(5))] : null;
  const img = poza ? null : IMAGINEA.get(cheie) || null;
  const k = img ? pozitia(refs, img.id) : -1;
  const n = lista.length;

  useEffect(() => { inchideRef.current?.focus(); }, []);
  useEffect(() => {
    const tasta = (e: KeyboardEvent) => {
      if (e.key === "Escape") inchide();
      if (e.key === "ArrowRight" && n > 1) laI((i + 1) % n);
      if (e.key === "ArrowLeft" && n > 1) laI((i - 1 + n) % n);
    };
    window.addEventListener("keydown", tasta);
    return () => window.removeEventListener("keydown", tasta);
  }, [i, n, inchide, laI]);

  if (!img && !(poza && poza.sursa === "incarcata")) return null;
  const src = img ? imagineMare(img.id)
    : srcPozaFatada(pid, poza as { poza?: string; imagine?: string }) ?? "";

  return (
    <div className="ft-lupa" role="dialog" aria-modal="true"
         onClick={(e) => { if (e.target === e.currentTarget) inchide(); }}>
      <div className="ft-lupa-in">
        <div className="ft-lupa-img">
          <img src={src} alt={img ? t(numeStil(img.stil)) : t("fat.lupa.poza_ta")} />
          {n > 1 && (
            <>
              <button type="button" className="ft-lupa-nav st" aria-label={t("fat.lupa.inapoi")}
                      onClick={() => laI((i - 1 + n) % n)}>‹</button>
              <button type="button" className="ft-lupa-nav dr" aria-label={t("fat.lupa.inainte")}
                      onClick={() => laI((i + 1) % n)}>›</button>
            </>
          )}
          <button ref={inchideRef} type="button" className="ft-lupa-x" aria-label={t("fat.lupa.inchide")}
                  onClick={inchide}><I.X size={14} /></button>
        </div>
        <div className="ft-lupa-panou">
          {img ? (
            <>
              <span className="lbl">{i + 1} / {n}</span>
              <b className="ft-lupa-titlu">{t(numeStil(img.stil))}</b>
              {img.proiect && (
                <span className="ft-proiect">
                  <b>{img.proiect.nume}</b> · {img.proiect.firma}
                  {img.proiect.oras && <> · {img.proiect.oras}</>}
                  {img.proiect.an && <>, {img.proiect.an}</>}
                </span>
              )}
              <span className="ft-nota">{t(`fat.stil.${img.stil}.desc` as Cheie)}</span>
              <span className="ft-paleta mare" aria-hidden="true">
                {PALETA[img.stil].map((c) => <i key={c} style={{ background: c }} />)}
              </span>
              {/* Ce se vede pe imaginea asta; fără etichete, ce ține de stil. */}
              {img.nivel || img.taguri?.length ? (
                <span className="ft-taguri">
                  {img.nivel && <span className="chip c-acc">{t(`tema.nivel.${img.nivel}` as Cheie)}</span>}
                  {img.taguri?.map((g) => <span key={g} className="chip c-mut">{t(`fat.tag.${g}` as Cheie)}</span>)}
                </span>
              ) : (
                <span className="ft-stil-mat">{t(`fat.stil.${img.stil}.mat` as Cheie)}</span>
              )}
              {potScrie && (
                <button type="button" className={`btn${k >= 0 ? "" : " btnp"}`}
                        disabled={k < 0 && refs.length >= MAX_REFERINTE}
                        onClick={() => alege(img.id)}>
                  {k >= 0 ? <><I.Bifa size={12} />{t("fat.lupa.aleasa", { n: k + 1 })}</>
                   : refs.length >= MAX_REFERINTE ? t("fat.lupa.plin") : t("fat.lupa.alege")}
                </button>
              )}
              {img.ai ? <span className="ft-credit">{t("fat.lupa.ai")}</span> : img.credit && (
                <span className="ft-credit">
                  {t("fat.lupa.foto")}{" "}
                  <a href={img.credit.link} target="_blank" rel="noopener noreferrer">{img.credit.autor}</a>
                  {img.credit.licenta && <>{" · "}{img.credit.licenta}</>}
                </span>
              )}
            </>
          ) : (
            <>
              <b className="ft-lupa-titlu">{t("fat.lupa.poza_ta")}</b>
              <span className="ft-nota">{t("fat.lupa.poza_ta_sub")}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
